<template>
  <v-container class="pt-3 pb-6 px-6">

    <!-- Floating Button -->
    <floating-button @click="save()" save :disabled="invalid" v-if="authUser"/>

    <!-- Page Header -->
    <page-header
      :title="isNew + ' PC Autorizado'"
      icon="mdi-laptop"
      divider="-"
      :breadcrumbs="breadcrumbs.items"
      refresh
      v-on:refresh="loadComputers()"
    ></page-header>

    <v-row align="center" justify="space-around">
      <v-col cols="12">
        <v-card>
          <!-- Loading Bar -->
          <v-progress-linear
            indeterminate
            v-if="isLoadingData"
          ></v-progress-linear>
          <!-- Content -->
          <v-card-text v-if="authUser">
            <v-container>
              <v-row align="center" justify="space-around">
                <!-- Name -->
                <v-col cols="4">
                  <v-text-field
                    v-model="computer_with_access.name"
                    :error-messages="nameErrors"
                    label="Nombre"
                    outlined
                    counter="32"
                    maxlength="32"
                    @blur="$v.computer_with_access.name.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- MAC Address -->
                <v-col cols="4">
                  <v-text-field
                    v-model="computer_with_access.mac_address"
                    :error-messages="macAddressErrors"
                    label="Dirección MAC"
                    hint="XX:XX:XX:XX:XX:XX"
                    persistent-hint
                    v-mask="'XX:XX:XX:XX:XX:XX'"
                    outlined
                    counter="17"
                    maxlength="17"
                    @blur="$v.computer_with_access.mac_address.$touch()"
                  ></v-text-field>
                </v-col>
                <!-- State -->
                <v-col cols="4">
                  <v-select
                    v-model="computer_with_access.is_active"
                    :error-messages="stateErrors"
                    :items="state"
                    label="Estado"
                    outlined
                    @blur="$v.computer_with_access.is_active.$touch()"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        style="cursor: pointer"
                        :color="data.item.value ? 'primary' : 'deep-orange'"
                        :class="{'white--text': !data.item.value}"
                      >{{ data.item.text }}</v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FloatingButton from "@/components/FloatingButton";
import PageHeader from "@/components/PageHeader";
import { mask } from 'vue-the-mask'
import { validationMixin } from "vuelidate";
import { required, macAddress } from "vuelidate/lib/validators";
import { ComputerWithAccessRepository } from "@/api/Repositories";
import Util from "@/util/index";

export default {
  components: {
    FloatingButton,
    PageHeader
  },
  directives: { mask },
  mixins: [validationMixin],
  validations: {
    computer_with_access: {
      name: { required },
      mac_address: { required, macAddress: macAddress(':') },
      is_active: { required }
    }
  },
  data: () => ({
    authUser: false,
    isLoadingData: false,
    oldName: '',
    oldMAC: '',
    computer_with_access: {
      name: "",
      mac_address: "",
      is_active: ""
    },
    alert: true,
    state: [
      { text: "Activo", value: true },
      { text: "Inactivo", value: false }
    ],
    breadcrumbs: {
      items: [
        { text: "Configuración", disabled: true },
        { text: "PC's Autorizados", disabled: true }
      ]
    }
  }),
  computed: {
    loading: {
      get () {
        return this.$store.state.loading
      },
      set (val) {
        this.$store.commit('loading', val)
      }
    },
    isNew () {
      return this.$route.params.id ? 'Modificar' : 'Añadir'
    },
    invalid () {
      return this.$v.$invalid && this.$v.$dirty
    },
    computers() {
      return this.$store.state.computers.computers;
    },
    uniqueName () {
      if (this.computer_with_access.name) {
        if (this.$route.params.id) {
          let nameExist = this.computers.some(element => {
            return element.name.trim() === this.computer_with_access.name.trim()
            && element.name.trim() !== this.oldName.trim()
          })
          return !nameExist
        } else {
          let nameExist = this.computers.some(element => {
            return element.name.trim() === this.computer_with_access.name.trim()
          })
          return !nameExist
        }
      } else {
        return true
      }
    },
    uniqueMacAddress () {
      if (this.computer_with_access.mac_address) {
        if (this.$route.params.id) {
          let macAddressExist = this.computers.some(element => {
            return element.mac_address === this.computer_with_access.mac_address
              && element.mac_address !== this.oldMAC
          })
          return !macAddressExist
        } else {
          let macAddressExist = this.computers.some(element => {
            return element.mac_address === this.computer_with_access.mac_address
          })
          return !macAddressExist
        }
      } else {
        return true
      }
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.computer_with_access.name.$dirty) return errors;
      !this.$v.computer_with_access.name.required && errors.push("Obligatorio.");
      !this.uniqueName && errors.push('PC ya existe.')
      return errors;
    },
    macAddressErrors() {
      const errors = [];
      if (!this.$v.computer_with_access.mac_address.$dirty) return errors;
      !this.$v.computer_with_access.mac_address.required && errors.push("Obligatorio.");
      !this.$v.computer_with_access.mac_address.macAddress && errors.push("Dirección MAC no valida.");
      !this.uniqueMacAddress && errors.push('Dirección MAC ya existe.')
      return errors;
    },
    stateErrors() {
      const errors = [];
      if (!this.$v.computer_with_access.is_active.$dirty) return errors;
      !this.$v.computer_with_access.is_active.required && errors.push("Obligatorio.");
      return errors;
    }
  },
  methods: {
    loadComputers () {
      this.isLoadingData = true
      this.$store.dispatch('computers/getComputers')
        .then(() => {
          this.getCurrentComputer()
        })
        .catch(error => {
          if (error.response.status === 403 ) {
            this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
              color: "error",
              x: "center",
              y: "top",
              icon: "mdi-information",
              timeout: 0,
              showClose: true,
              closeText: "Cerrar"
            });
            this.$router.push({ name: 'home'})
          } else {
            var errorToastMessage = "Ha ocurrido un error cargando los PCs autorizados."
            Util.showErrorToast(this, error, errorToastMessage);
            this.computerList();
          }
        })
    },
    getCurrentComputer() {
      var id = this.$route.params.id;
      if (id) {
        ComputerWithAccessRepository.get(id)
          .then(response => {
            var data = response.data.data

            this.computer_with_access = data
            this.oldName = data.name
            this.oldMAC = data.mac_address
          })
          .catch(error => {
            if (error.response.status === 403 ) {
              this.$toast("Su perfil de usuario no tiene los permisos suficientes para acceder a esta funcionalidad del sistema.", {
                color: "error",
                x: "center",
                y: "top",
                icon: "mdi-information",
                timeout: 0,
                showClose: true,
                closeText: "Cerrar"
              });
              this.$router.push({ name: 'home'})
            } else {
              var errorToastMessage = "Ha ocurrido un error cargando el PC autorizado."
              Util.showErrorToast(this, error, errorToastMessage);
            }
            this.computerList();
          })
      } else {
        this.computer_with_access = {
          name: "",
          mac_address: "",
          is_active: ""
        };
      }
      this.$v.$reset()
      this.authUser = true
      this.isLoadingData = false
    },
    computerList() {
      this.$router.push({
        name: "computer-list"
      });
    },
    save () {
      this.$route.params.id ? this.editComputer() : this.createComputer()
    },
    createComputer() {
      this.$v.$touch();
      if (!this.$v.$invalid && this.uniqueName && this.uniqueMacAddress) {
        this.loading = true
        this.$store.dispatch("computers/createComputer", this.computer_with_access)
          .then(() => {
            this.$toast("PC autorizado creado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.computerList();
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error creando el PC autorizado."
            Util.showErrorToast(this, error, errorToastMessage);
            console.log(error);
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    editComputer() {
      this.$v.$touch();
      if (!this.$v.$invalid && this.uniqueName && this.uniqueMacAddress) {
        this.loading = true
        this.$store.dispatch("computers/editComputer", this.computer_with_access)
          .then(() => {
            this.$toast("PC autorizado modificado correctamente.", {
              color: "success",
              x: "center",
              y: "top",
              icon: "mdi-information"
            });
            this.computerList();
          })
          .catch(error => {
            var errorToastMessage = "Ha ocurrido un error modificando el PC autorizado."
            Util.showErrorToast(this, error, errorToastMessage);
          })
          .finally(() => {
            this.loading = false
          })
      }
    }
  },
  created() {
    this.loadComputers();
  }
};
</script>
